import { useMemo } from 'react';
import { StyleSheet } from 'react-native';

import { tokens } from '../../tokens';
import { XStack, type XStackProps } from '../stack';

import { useQuantity } from './context';
import { QuantityCounterBox, type QuantityCounterBoxProps } from './quantity-box';
import { QuantityStepperButton } from './quantity-stepper-button';

type QuantityStepperProps = Partial<XStackProps> & {
  valueBoxProps?: QuantityCounterBoxProps;
  decrementDisabled?: boolean;
  incrementDisabled?: boolean;
  incrementSoftDisabled?: boolean;
  isLoading?: boolean;
  onIncrementSoftDisabledPress?: () => void;
};

export function QuantityStepper({
  valueBoxProps,
  decrementDisabled,
  incrementDisabled,
  incrementSoftDisabled,
  onIncrementSoftDisabledPress,
  isLoading = false,
  ...containerProps
}: QuantityStepperProps) {
  const { accessibilityHint, label, disabled, value, onChangeValue, options } = useQuantity();

  const selectedOptionsIndex = useMemo(
    () => options.findIndex(opt => opt.value === value) ?? 0,
    [options, value]
  );

  const selectedOption = (options[selectedOptionsIndex] || options[0]) ?? {
    label: null,
    value: null,
  };

  const isDecrementDisabled = disabled || decrementDisabled || selectedOptionsIndex <= 0;
  const isIncrementDisabled =
    disabled || incrementDisabled || selectedOptionsIndex >= options.length - 1;

  const handleIncrement = () => {
    if (!isIncrementDisabled && !incrementSoftDisabled && !isLoading) {
      onChangeValue(options[selectedOptionsIndex + 1].value);
    }
  };

  const handleDecrement = () => {
    if (!isDecrementDisabled) {
      onChangeValue(options[selectedOptionsIndex - 1].value);
    }
  };

  return (
    <XStack
      accessible
      role="spinbutton"
      aria-disabled={disabled}
      aria-valuetext={String(value)}
      aria-label={label}
      accessibilityHint={accessibilityHint}
      accessibilityActions={[
        { name: 'increment', label: 'increment' },
        { name: 'decrement', label: 'decrement' },
      ]}
      onAccessibilityAction={event => {
        switch (event.nativeEvent.actionName) {
          case 'increment': {
            handleIncrement();
            break;
          }
          case 'decrement': {
            handleDecrement();
            break;
          }
        }
      }}
      {...containerProps}
      style={[styles.container, containerProps.style]}
    >
      <QuantityStepperButton
        disabled={isDecrementDisabled}
        iconName="REMOVE"
        handlePress={() => {
          handleDecrement();
        }}
        testID="decrement-button-for-number-input"
      />

      {isLoading ? (
        <XStack style={styles.quantitySkeleton} />
      ) : (
        <QuantityCounterBox value={selectedOption.label} {...valueBoxProps} />
      )}

      <QuantityStepperButton
        disabled={isIncrementDisabled}
        softDisabled={incrementSoftDisabled}
        iconName="ADD"
        handleSoftDisabledPress={onIncrementSoftDisabledPress}
        handlePress={() => {
          handleIncrement();
        }}
        testID="increment-button-for-number-input"
      />
    </XStack>
  );
}

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
  },
  incrementSoftDisabledMessage: {
    marginLeft: 5,
    color: 'white',
  },
  quantitySkeleton: {
    width: 50,
    height: 16,
    borderRadius: 12,
    marginHorizontal: 8,
    backgroundColor: tokens.colors.$houseLight,
  },
});
